import {
  Box,
  Flex,
  ScaleFade,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useMediaQuery,
} from "@chakra-ui/react";
import { aiScoresGroup, preferenceScoreGroup } from "config/Consts";
import { IScoresInformationTableData } from "Interfaces/Job";
import { useGetUserProfileQuery } from "queries";
import { useGetScoresQuery } from "queries/Jobs";
import { FC, useMemo, useState } from "react";

const getSortedScores = (aiScores: any): IScoresInformationTableData[] => {
  let arr: any[] = [];
  if (!(aiScores as boolean)) return arr;

  const sortedArr = [...Object.entries(aiScores)].sort(
    (a: any[], b: any[]) => b[1] - a[1]
  );
  arr = sortedArr.map((el) => ({ occupation: el[0], score: el[1] }));
  return arr;
};

const ScoresInformation: FC<{ alreadyOpen?: boolean }> = ({
  alreadyOpen = false,
}) => {
  const [isOpen, setOpen] = useState(alreadyOpen);
  const [applyMaxWidth] = useMediaQuery("(max-width:500px)");

  const { data: scores }: any = useGetScoresQuery();
  const { data: userData } = useGetUserProfileQuery();

  const sortedScoresArr = useMemo(() => {
    return getSortedScores(scores?.aiScore);
  }, [scores?.aiScore]);

  let prevRank: number;
  const getRank = (sortedArr: any[], idx: number): number => {
    try {
      if (sortedArr[idx - 1].score === sortedArr[idx].score) {
        return prevRank;
      }
    } catch (e) {
      return idx + 1;
    }

    return idx + 1;
  };
  return (
    <Box>
      {isOpen && (
        <ScaleFade initialScale={0.9} in={isOpen}>
          {preferenceScoreGroup.includes(userData?.group) &&
            !aiScoresGroup.includes(userData?.group) && (
              <>
                <Text fontSize={[14, 14, 16]} mb={4}>
                  The scores you see are based on the preferences you specified
                  in the personality survey. The higher the score, the better
                  that job matches your preferences as we understand them.
                  <br />
                  <br />
                </Text>
              </>
            )}
          {aiScoresGroup.includes(userData?.group) &&
            !preferenceScoreGroup.includes(userData?.group) && (
              <Box
                /* maxW={applyMaxWidth ? "90vw" : "initial"} */
                fontSize={[14, 14, 16]}
              >
                <Text fontSize={[14, 14, 16]}>
                  We compared your survey responses to hundreds of other
                  respondents and ranked the jobs you see here according to your
                  predicted performance in each occupation. Here are nine
                  occupations in which people with similar skills and
                  personalities as you tend to be most successful:
                </Text>
                <Flex
                  alignItems={{ sm: "flex-start", lg: "flex-end" }}
                  flexWrap="wrap"
                  direction={{ sm: "column", lg: "row" }}
                  mt={4}
                >
                  {(scores?.aiScore as boolean) && (
                    <TableContainer flex={3} maxW="100vw" my={4}>
                      <Table minH="20rem">
                        <Thead>
                          <Tr
                            bg="teal.600"
                            sx={{
                              "& th": {
                                color: "white",
                              },
                            }}
                          >
                            <Th>Rank</Th>
                            <Th>Occupation</Th>
                            <Th>AI Score Percentile</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {sortedScoresArr.map(
                            (information: any, idx: number) => {
                              prevRank = getRank(sortedScoresArr, idx);
                              const isNa = information.occupation === "Other";

                              return (
                                <Tr key={idx}>
                                  <Td>{isNa ? "N/A" : prevRank}</Td>
                                  <Td>
                                    {information.occupation === "Healthcare"
                                      ? "Orderly/Personal Care"
                                      : information.occupation}
                                  </Td>
                                  <Td>
                                    {isNa
                                      ? "N/A"
                                      : information.score
                                          ?.toString()
                                          ?.concat("%")}
                                  </Td>
                                </Tr>
                              );
                            }
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                  <Text flex={2} fontSize={[14, 14, 16]}>
                    A ranking of 1 means that we think you’ll be most successful
                    in that occupation. Similarly, the higher your percentile,
                    the better you are predicted to perform in that occupation.
                    For example, being in the 75th percentile means we predict
                    you would do better than 75% of the population and only 25%
                    of people would do better than you.
                  </Text>
                </Flex>
              </Box>
            )}
          {aiScoresGroup.includes(userData?.group) &&
            preferenceScoreGroup.includes(userData?.group) && (
              <>
                <Box
                  /* maxW={applyMaxWidth ? "90vw" : "initial"} */ fontSize={[
                    14, 14, 16,
                  ]}
                >
                  <Text>
                    We compared your survey responses to hundreds of other
                    respondents and ranked the jobs you see here accordingly.
                  </Text>
                  <ul className="list-disc ml-6">
                    <li>
                      The <span className="text-[#009BF2] font-bold">blue</span>{" "}
                      scores are based on the preferences you specified in the
                      personality survey.
                    </li>
                    <li>
                      The <span className="text-[#E4E400] font-bold">lime</span>{" "}
                      scores rank occupations based on your survey responses.
                    </li>
                  </ul>
                  <Text>
                    Here are nine occupations in which people with similar
                    skills and personalities as you tend to be most successful:
                  </Text>
                  <Flex
                    alignItems={{ sm: "flex-start", lg: "flex-end" }}
                    flexWrap="wrap"
                    direction={{ sm: "column", lg: "row" }}
                    mt={4}
                  >
                    {(scores?.aiScore as boolean) && (
                      <TableContainer flex={3} maxW="100vw" my={4}>
                        <Table minH="20rem">
                          <Thead>
                            <Tr
                              bg="teal.600"
                              sx={{
                                "& th": {
                                  color: "white",
                                },
                              }}
                            >
                              <Th>Rank</Th>
                              <Th>Occupation</Th>
                              <Th>AI Score Percentile</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {sortedScoresArr.map(
                              (information: any, idx: number) => {
                                prevRank = getRank(sortedScoresArr, idx);
                                const isNa = information.occupation === "Other";

                                return (
                                  <Tr key={idx}>
                                    <Td>{isNa ? "N/A" : prevRank}</Td>
                                    <Td>
                                      {information.occupation === "Healthcare"
                                        ? "Orderly/Personal Care"
                                        : information.occupation}
                                    </Td>
                                    <Td>
                                      {isNa
                                        ? "N/A"
                                        : information.score
                                            ?.toString()
                                            ?.concat("%")}
                                    </Td>
                                  </Tr>
                                );
                              }
                            )}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                    <Text flex={2} fontSize={[14, 14, 16]}>
                      A ranking of 1 means that we think {"you'll"} be most
                      successful in that occupation. Similarly, the higher your
                      percentile, the better you are predicted to perform in
                      that occupation. For example, being in the 75th percentile
                      means we predict you would do better than 75% of the
                      population and only 25% of people would do better than
                      you.
                      <br />
                      <br />
                    </Text>
                  </Flex>
                </Box>
              </>
            )}
        </ScaleFade>
      )}
    </Box>
  );
};

export default ScoresInformation;
